import { createRouter, createWebHistory, type RouteRecordRaw } from "vue-router";
import { useAuthStore } from "./stores/auth";

/**
 * Routes are lazily imported so a TV downloads the display bundle and nothing else — the
 * admin console is the largest chunk and a kiosk must never pay for it.
 *
 * The auth guard is a *convenience*, not a security boundary: every admin API route enforces
 * its own role check server-side. Redirecting here only saves the user a failed request.
 */
const DEFAULT_VENUE = "the-exchange";

const routes: RouteRecordRaw[] = [
  { path: "/", redirect: `/v/${DEFAULT_VENUE}` },

  /**
   * `/demo` used to be a separate view with its own simulator. There is now one board, and
   * whether it offers simulator controls is a property of the *venue* (`testMode`), toggled
   * from the admin console — not of the URL.
   *
   * Two systems meant two things to keep in step, and worse, a venue could be live while a
   * `/demo` URL still moved its real prices. Kept as a redirect because it is written down
   * in docs, commit messages and probably a browser bookmark.
   */
  { path: "/demo", redirect: `/v/${DEFAULT_VENUE}` },

  {
    // Before /display/:token, or "demo" would be captured as a token.
    path: "/display/demo",
    name: "display-demo",
    component: async () => import("./views/DisplayDemoView.vue"),
  },
  {
    path: "/display/:token",
    name: "display",
    component: async () => import("./views/DisplayView.vue"),
  },

  {
    /**
     * Self-serve ordering for table tablets and customers' own phones. Deliberately its
     * own route rather than a mode of `/v/:slug`: this one takes payment, and it is the
     * only page allowed to load Square's payment SDK under the CSP.
     */
    path: "/order/:slug",
    name: "order",
    component: () => import("@/views/OrderView.vue"),
  },
  {
    /** The one board. Simulator controls appear only when the venue is in demo mode. */
    path: "/v/:slug",
    name: "board",
    component: async () => import("./views/DemoView.vue"),
  },
  {
    path: "/bar",
    name: "bar",
    component: async () => import("./views/BarView.vue"),
  },

  {
    path: "/admin/login",
    name: "admin-login",
    component: async () => import("./views/admin/LoginView.vue"),
    meta: { public: true },
  },
  {
    // The magic link lands here; the login view redeems the token and redirects.
    path: "/admin/verify",
    redirect: (to) => ({ path: "/admin/login", query: to.query }),
  },
  {
    path: "/admin",
    component: async () => import("./views/admin/AdminShell.vue"),
    meta: { requiresAuth: true },
    children: [
      {
        path: "",
        name: "admin-dashboard",
        component: async () => import("./views/admin/DashboardView.vue"),
      },
      {
        path: "drinks",
        name: "admin-drinks",
        component: async () => import("./views/admin/DrinksView.vue"),
      },
      {
        path: "market",
        name: "admin-market",
        component: async () => import("./views/admin/MarketView.vue"),
      },
      {
        path: "displays",
        name: "admin-displays",
        component: async () => import("./views/admin/DisplaysView.vue"),
      },
      {
        path: "reports",
        name: "admin-reports",
        component: async () => import("./views/admin/ReportsView.vue"),
      },
      {
        path: "users",
        name: "admin-users",
        component: async () => import("./views/admin/UsersView.vue"),
      },
    ],
  },

  // Unknown paths land on the demo rather than a 404: every entry point to this app is
  // something someone was handed (a QR code, a link), so the friendliest failure is the board.
  { path: "/:pathMatch(.*)*", redirect: "/demo" },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to) => {
  if (to.meta.requiresAuth !== true) return true;

  const auth = useAuthStore();
  // Resolve the session once per page load rather than on every navigation.
  if (!auth.checked) await auth.refresh();
  if (auth.isAuthenticated) return true;

  return { path: "/admin/login", query: { next: to.fullPath } };
});
